import { Avatar, Button, Heading, Input, Link, Modal, ModalClose, Stack, Text, View, useInput } from '@fold-dev/core'
import { useRef, useState } from 'react'
import { Error, Loading } from '../components'
import { logger, validEmail } from '../helpers/util'
import { useWorkspaceInvites, useWorkspaces } from '../hooks/api.hooks'
import { useFile } from '../hooks/file.hook'
import { Logo, LogoF, LogoNew, NewLogoLined } from '../logo'
import { useNavigate } from 'react-router'

export default function WorkspaceOnboardingModal({ onDismiss, onCreate }) {
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<any>(null)
    const [step, setStep] = useState<'start' | 'image' | 'invite' | 'created'>('start')
    const [image, setImage] = useState('')
    const [workspace, setWorkspace] = useState<any>({})
    const [name, setName] = useState('')
    const email1 = useInput('')
    const email2 = useInput('')
    const email3 = useInput('')
    const email4 = useInput('')
    const fileRef = useRef<any>(null)
    const { upload } = useFile()
    const workspaces = useWorkspaces()
    const workspaceInvites = useWorkspaceInvites()
    const { getStorableUrl } = useFile()

    const handleFileChange = async (e) => {
        if (e.target.files.length == 0) return

        setLoading(true)
        setError(null)

        try {
            const { url } = await upload({
                folder: '',
                file: e.target.files[0],
                secure: true,
            })

            setImage(url)
            setLoading(false)
        } catch (e) {
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleCreate = async () => {
        setLoading(true)
        setError(false)

        try {
            const workspace = await workspaces.create({
                name,
                image: getStorableUrl(image),
            })

            setWorkspace(workspace)
            setLoading(false)
            setStep('invite')
            onCreate()

            // dispatch(createTeam(data.createTeam))
            // MessagingService.getInstance().join(data.createTeam.id)
            navigate(`/app/workspace/${workspace.id}/inbox`)
        } catch (e) {
            logger(e)
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleInvites = async () => {
        setLoading(true)
        setError(null)

        try {
            const emails: any = []

            // Check all our 4 email addresses here
            // If they are not black then they must be valid
            // Otherwise don't add them to be invited
            if (email1.value != '') {
                if (!validEmail(email1.value)) return setError('Only valid emails are accepted')
                emails.push(email1.value)
            }

            if (email2.value != '') {
                if (!validEmail(email2.value)) return setError('Only valid emails are accepted')
                emails.push(email2.value)
            }

            if (email3.value != '') {
                if (!validEmail(email3.value)) return setError('Only valid emails are accepted')
                emails.push(email3.value)
            }

            if (email4.value != '') {
                if (!validEmail(email4.value)) return setError('Only valid emails are accepted')
                emails.push(email4.value)
            }

            if (emails.length == 0) return setLoading(false)

            await workspaceInvites.create(workspace.id, { emails })

            setLoading(false)
            setStep('created')
        } catch (e) {
            setLoading(false)
            setError(typeof e == 'string' ? e : 'An error occured')
        }
    }

    const handleFileClick = (e) => (fileRef.current ? fileRef.current.click() : null)

    return (
        <Modal
            width="100%"
            height="100%"
            radius={0}
            border="none"
            isVisible={true}
            onDismiss={onDismiss}>
            <ModalClose onClick={onDismiss} />
            <Loading loading={loading} />

            <View
                row
                position="absolute"
                style={{ inset: 0 }}>
                <View
                    row
                    height="100%"
                    flex={1}
                    bgToken="surface-strong">
                    <LogoNew color="var(--f-color-surface-strongest)" />
                </View>

                <View
                    column
                    height="100%"
                    width="100%"
                    p="5rem 0"
                    flex={2}>
                    {step == 'start' && (
                        <Stack
                            direction="vertical"
                            spacing={20}
                            noStretch>
                            <Heading
                                fontSize="3rem"
                                textAlign="center">
                                Create a Workspace
                            </Heading>

                            <Text
                                size="lg"
                                textAlign="center">
                                What would you like to call your new workspace?
                            </Text>

                            <Input
                                placeholder="Workspace name"
                                size="lg"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                            />

                            <Button
                                onClick={() => setStep('image')}
                                size="xl"
                                variant="accent"
                                disabled={!name || loading}>
                                Next
                            </Button>

                            <Link onClick={onDismiss}>No thanks, don't create a new workspace</Link>
                        </Stack>
                    )}

                    {step == 'image' && (
                        <Stack
                            direction="vertical"
                            spacing={20}
                            noStretch>
                            <input
                                accept="image/png,image/jpg"
                                type="file"
                                className="hide"
                                ref={fileRef}
                                onChange={handleFileChange}
                            />

                            <Avatar
                                src={image}
                                name={name}
                                size="xl"
                                width={150}
                                height={150}
                                className="f-buttonize"
                                onClick={handleFileClick}
                            />

                            <Heading
                                fontSize="3rem"
                                textAlign="center">
                                {name}
                            </Heading>

                            <Link
                                size="lg"
                                textAlign="center"
                                onClick={handleFileClick}>
                                Choose an image for your new workspace
                            </Link>

                            <View row>
                                <Button
                                    onClick={() => setStep('start')}
                                    size="xl"
                                    variant="accent">
                                    Back
                                </Button>
                                &nbsp;
                                <Button
                                    onClick={handleCreate}
                                    size="xl"
                                    variant="accent"
                                    disabled={loading || !name}>
                                    Create Workspace
                                </Button>
                            </View>

                            <Link onClick={onDismiss}>No thanks, don't create a new workspace</Link>
                        </Stack>
                    )}

                    {step == 'invite' && (
                        <Stack
                            direction="vertical"
                            spacing={20}
                            noStretch>
                            <Heading
                                fontSize="3rem"
                                textAlign="center">
                                Invite People
                            </Heading>

                            <Text
                                textAlign="center"
                                size="lg">
                                Your workspace has been successfully created.
                            </Text>

                            <Text textAlign="center">Invite some people by entering their email addresses.</Text>

                            <Input
                                placeholder="Email address one"
                                size="lg"
                                value={email1.value}
                                onChange={email1.onChange}
                            />

                            <Input
                                placeholder="Email address two"
                                size="lg"
                                value={email2.value}
                                onChange={email2.onChange}
                            />

                            <Input
                                placeholder="Email address three"
                                size="lg"
                                value={email3.value}
                                onChange={email3.onChange}
                            />

                            <Input
                                placeholder="Email address four"
                                size="lg"
                                value={email4.value}
                                onChange={email4.onChange}
                            />

                            <Button
                                onClick={handleInvites}
                                size="xl"
                                variant="accent"
                                disabled={loading}>
                                Invite
                            </Button>

                            <Link onClick={onDismiss}>Invite people later</Link>
                        </Stack>
                    )}

                    {step == 'created' && (
                        <Stack
                            direction="vertical"
                            spacing={20}
                            noStretch>
                            <View>
                                <Avatar
                                    src={image}
                                    size="xl"
                                    width={150}
                                    height={150}
                                    name={name}
                                />
                            </View>

                            <Heading
                                fontSize="3rem"
                                textAlign="center">
                                Congratulations
                            </Heading>

                            <Text
                                size="lg"
                                textAlign="center">
                                You've just created a workspace.
                            </Text>

                            <Button
                                onClick={onDismiss}
                                size="xl"
                                variant="accent">
                                Get Started
                            </Button>
                        </Stack>
                    )}

                    <Error error={error} />
                </View>
            </View>
        </Modal>
    )
}
